import React, { useCallback, useRef, useState } from 'react';

import { ConfirmationDialog } from '../components/ConfirmationDialog';

export type ConfirmationDialogOptions = {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'primary' | 'danger';
  hideCancelButton?: boolean;
  showWarningIcon?: boolean;
  cancelButtonStyle?: 'default' | 'clear';
};

type PendingConfirmation = ConfirmationDialogOptions & {
  resolve: (confirmed: boolean) => void;
  alertOnly?: boolean;
};

/**
 * Cross-platform confirmation — uses {@link ConfirmationDialog} on web and native.
 */
export function useConfirmationDialog(options?: { inline?: boolean }) {
  const inline = options?.inline ?? false;
  const pendingRef = useRef<PendingConfirmation | null>(null);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  const confirm = useCallback(
    (options: ConfirmationDialogOptions): Promise<boolean> => {
      return new Promise(resolve => {
        const entry: PendingConfirmation = { ...options, resolve };
        pendingRef.current = entry;
        setPending(entry);
      });
    },
    [],
  );

  /** Informational modal (OK only) — works on web; prefer over `Alert.alert`. */
  const alert = useCallback(
    (
      options: Pick<
        ConfirmationDialogOptions,
        'title' | 'message' | 'confirmText' | 'showWarningIcon'
      >,
    ): Promise<void> => {
      return new Promise(resolve => {
        const entry: PendingConfirmation = {
          title: options.title,
          message: options.message,
          confirmText: options.confirmText ?? 'OK',
          confirmButtonStyle: 'primary',
          hideCancelButton: true,
          showWarningIcon: options.showWarningIcon ?? true,
          alertOnly: true,
          resolve: () => resolve(),
        };
        pendingRef.current = entry;
        setPending(entry);
      });
    },
    [],
  );

  const close = useCallback(() => {
    const current = pendingRef.current;
    if (current) {
      current.resolve(current.alertOnly ? true : false);
    }
    pendingRef.current = null;
    setPending(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    const current = pendingRef.current;
    if (!current) {
      return;
    }
    current.resolve(true);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirmationDialog = pending ? (
    <ConfirmationDialog
      visible
      inline={inline}
      onClose={close}
      onConfirm={handleConfirm}
      title={pending.title}
      message={pending.message}
      confirmText={pending.confirmText}
      cancelText={pending.cancelText}
      confirmButtonStyle={pending.confirmButtonStyle}
      hideCancelButton={pending.hideCancelButton}
      showWarningIcon={pending.showWarningIcon}
      cancelButtonStyle={pending.cancelButtonStyle}
    />
  ) : null;

  return { confirm, alert, confirmationDialog };
}
